import type {
  IJsonRowNode,
  IJsonTabNode,
  IJsonTabSetNode,
} from "flexlayout-react";
import type { Config } from "./layout-active-context";

export type TabNode = {
  type: "tab";
  id?: string;
  panelTypeId?: string;
};

export type RowNode = {
  type: "row";
  id?: string;
  weight?: number;
  children: (RowNode | TabsetNode)[];
};

export type TabsetNode = {
  type: "tabset";
  id?: string;
  weight?: number;
  selected?: number;
  children: TabNode[];
};

export type Tree = RowNode;

type Schema = {
  tree: Tree;
  id: string;
  name: string;
};

export type Layout = {
  version: "1";
  data: Schema;
};

const serializeTabNode = (node: TabNode, config: Config): IJsonTabNode => {
  let name: string | undefined;
  let tabConfig: Record<string, unknown> | undefined;
  if (node.panelTypeId) {
    const panelConfig = config[node.panelTypeId];
    if (panelConfig) {
      name = panelConfig.name;
      if (panelConfig.enableMaximize !== undefined) {
        tabConfig = { enableMaximize: panelConfig.enableMaximize };
      }
    }
  }

  return {
    type: "tab",
    id: node.id,
    component: node.panelTypeId,
    name: name,
    config: tabConfig,
  };
};

const serializeRowChild = (
  node: TabsetNode | RowNode,
  config: Config
): IJsonTabSetNode | IJsonRowNode => {
  if (node.type === "tabset") {
    // Check if any child tab has size constraints
    let minWidth: number | undefined;
    let maxWidth: number | undefined;
    let minHeight: number | undefined;
    let maxHeight: number | undefined;
    for (const child of node.children) {
      if (child.panelTypeId) {
        const panelConfig = config[child.panelTypeId];
        if (panelConfig?.minWidth !== undefined) {
          minWidth = panelConfig.minWidth;
        }
        if (panelConfig?.maxWidth !== undefined) {
          maxWidth = panelConfig.maxWidth;
        }
        if (panelConfig?.minHeight !== undefined) {
          minHeight = panelConfig.minHeight;
        }
        if (panelConfig?.maxHeight !== undefined) {
          maxHeight = panelConfig.maxHeight;
        }
      }
    }

    return {
      type: "tabset",
      weight: node.weight,
      selected: node.selected,
      id: node.id,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      children: node.children.map((child) => serializeTabNode(child, config)),
    };
  }

  return serializeRowNode(node, config);
};

const serializeRowNode = (node: RowNode, config: Config): IJsonRowNode => {
  return {
    type: "row",
    weight: node.weight,
    id: node.id,
    children: node.children.map((child) => serializeRowChild(child, config)),
  };
};

export const serialize = (tree: Tree, config: Config): IJsonRowNode => {
  return serializeRowNode(tree, config);
};

const isJsonTabSetNode = (
  node: IJsonRowNode | IJsonTabSetNode
): node is IJsonTabSetNode => {
  return node.type === "tabset";
};

const deserializeTabNode = (node: IJsonTabNode): TabNode => {
  return {
    type: "tab",
    id: node.id,
    panelTypeId: node.component,
  };
};

const deserializeRowChild = (
  node: IJsonRowNode | IJsonTabSetNode
): RowNode | TabsetNode => {
  if (isJsonTabSetNode(node)) {
    return {
      type: "tabset",
      id: node.id,
      weight: node.weight,
      selected: node.selected,
      children: node.children.map(deserializeTabNode),
    };
  }

  return deserializeRowNode(node);
};

const deserializeRowNode = (node: IJsonRowNode): RowNode => {
  return {
    type: "row",
    id: node.id,
    weight: node.weight,
    children: node.children.map(deserializeRowChild),
  };
};

export const deserialize = (node: IJsonRowNode): Tree => {
  return deserializeRowNode(node);
};

const findPanelTypeIdsFromNode = (
  ids: Set<string>,
  node: RowNode | TabsetNode | TabNode
) => {
  if (node.type === "tab") {
    if (node.panelTypeId) {
      ids.add(node.panelTypeId);
    }

    return;
  }

  for (const child of node.children) {
    findPanelTypeIdsFromNode(ids, child);
  }
};

export const findPanelTypeIds = (tree: Tree) => {
  const ids = new Set<string>();
  findPanelTypeIdsFromNode(ids, tree);

  return ids;
};
